// analytics.ts - Unified analytics service for cross-platform tracking

import { v4 as uuidv4 } from "uuid"

// Define account types
export type AnalyticsAccountType = "main" | "event"

// Interface for analytics events
export interface AnalyticsEvent {
    name: string
    properties?: Record<string, any>
    accountType?: AnalyticsAccountType
    eventId?: string
}

// Interface for user identification
export interface UserIdentification {
    userId: string
    traits?: Record<string, any>
}

// Analytics providers
export enum AnalyticsProvider {
    GOOGLE_ANALYTICS = "google-analytics",
    FACEBOOK_PIXEL = "facebook-pixel",
    TIKTOK_PIXEL = "tiktok-pixel",
    SNAPCHAT_PIXEL = "snapchat-pixel",
    MICROSOFT_CLARITY = "microsoft-clarity",
    POSTHOG = "posthog",
}

/**
 * Analytics service that handles user identification and event tracking
 * across multiple platforms and account types.
 */
class AnalyticsService {
    private mainUserId: string | null = null
    private eventUserIds: Record<string, string> = {}
    private initialized = false
    private apiEndpoint: string | null = null

    constructor() {
        // Initialize the service when instantiated
        if (typeof window !== "undefined") {
            this.initialize()
        }
    }

    /**
     * Initialize the analytics service
     */
    public initialize(): void {
        if (this.initialized) return

        // Set API endpoint for sending analytics data
        this.apiEndpoint =
            process.env.NEXT_PUBLIC_ANALYTICS_API_ENDPOINT || "/api/analytics"

        // Generate or retrieve the main user ID
        this.mainUserId = this.getOrGenerateMainUserId()

        // Mark as initialized
        this.initialized = true

        // Send the main user ID to the API
        // this.syncUserIdToApi(this.mainUserId, "main")
    }

    /**
     * Get or generate the main user ID, stored in localStorage
     */
    private getOrGenerateMainUserId(): string {
        if (typeof window === "undefined") return ""

        const storageKey = "analytics_main_user_id"
        const storedId = localStorage.getItem(storageKey)

        if (storedId) {
            return storedId
        }

        // Generate a new UUID for the user
        const newId = uuidv4()
        localStorage.setItem(storageKey, newId)
        return newId
    }

    /**
     * Get or generate an event-specific user ID, stored in localStorage
     */
    private getOrGenerateEventUserId(eventId: string): string {
        if (typeof window === "undefined" || !eventId) return ""

        const storageKey = `analytics_event_user_id_${eventId}`
        const storedId = localStorage.getItem(storageKey)

        if (storedId) {
            this.eventUserIds[eventId] = storedId
            return storedId
        }

        // Generate a new UUID for the event user
        const newId = uuidv4()
        localStorage.setItem(storageKey, newId)
        this.eventUserIds[eventId] = newId
        return newId
    }

    /**
     * Send the user ID to the API for server-side tracking
     */
    // private syncUserIdToApi(
    //     userId: string,
    //     accountType: AnalyticsAccountType,
    //     eventId?: string
    // ): void {
    //     if (!this.apiEndpoint || !userId) return

    //     try {
    //         fetch(this.apiEndpoint, {
    //             method: "POST",
    //             headers: {
    //                 "Content-Type": "application/json",
    //             },
    //             body: JSON.stringify({
    //                 type: "identify",
    //                 userId,
    //                 accountType,
    //                 eventId,
    //                 timestamp: new Date().toISOString(),
    //             }),
    //         }).catch((e) => console.error("Error syncing user ID to API:", e))
    //     } catch (error) {
    //         console.error("Error syncing user ID to API:", error)
    //     }
    // }

    /**
     * Identify a user across analytics platforms
     */
    public identify(
        identification: UserIdentification,
        accountType: AnalyticsAccountType = "main",
        eventId?: string
    ): void {
        if (!this.initialized) this.initialize()

        const userId =
            identification.userId ||
            (accountType === "main"
                ? this.mainUserId
                : this.getEventUserId(eventId))

        if (!userId) return

        // Track in PostHog
        if (window["posthog"]) {
            if (accountType === "main" || !eventId) {
                window["posthog"].identify(userId, identification.traits)
            }
        }

        // Track in other providers as needed
        // this.syncUserIdToApi(userId, accountType, eventId)
    }

    /**
     * Track an event across analytics platforms
     */
    public track(event: AnalyticsEvent): void {
        if (!this.initialized) this.initialize()

        const { name, properties = {}, accountType = "main", eventId } = event
        const userId =
            accountType === "main"
                ? this.mainUserId
                : this.getEventUserId(eventId)

        if (!userId) return

        // Add user ID to properties
        const enhancedProperties = {
            ...properties,
            userId,
            accountType,
            ...(eventId ? { eventId } : {}),
        }

        // Track in Google Analytics
        if (window["gtag"]) {
            window["gtag"]("event", name, enhancedProperties)
        }

        // Track in Facebook Pixel
        if (window["fbq"]) {
            window["fbq"]("trackCustom", name, enhancedProperties)
        }

        // Track in TikTok Pixel
        if (window["ttq"]) {
            window["ttq"].track(name, enhancedProperties)
        }

        // Track in Snapchat Pixel
        if (window["snaptr"]) {
            window["snaptr"]("track", name, enhancedProperties)
        }

        // Track in PostHog
        if (window["posthog"]) {
            window["posthog"].capture(name, enhancedProperties)
        }

        // Send to our own API for server-side tracking
        // this.trackToApi(name, enhancedProperties, accountType, eventId)
    }

    /**
     * Track a page view across analytics platforms
     */
    public pageView(
        url: string,
        accountType: AnalyticsAccountType = "main",
        eventId?: string,
        userProperties?: Record<string, any>
    ): void {
        if (!this.initialized) this.initialize()

        const userId =
            accountType === "main"
                ? this.mainUserId
                : this.getEventUserId(eventId)

        if (!userId) return

        const properties = {
            url,
            userId,
            accountType,
            ...(eventId ? { eventId } : {}),
            ...(userProperties || {}),
        }

        // Track in Google Analytics
        if (window["gtag"]) {
            window["gtag"]("event", "page_view", properties)
        }

        // Track in Facebook Pixel
        if (window["fbq"]) {
            window["fbq"]("track", "PageView", properties)
        }

        // Track in TikTok Pixel
        if (window["ttq"]) {
            window["ttq"].page(properties)
        }

        // Track in Snapchat Pixel
        if (window["snaptr"]) {
            window["snaptr"]("track", "PAGE_VIEW", properties)
        }

        // Track in PostHog
        if (window["posthog"]) {
            window["posthog"].capture("$pageview", properties)
        }

        // Send to our own API for server-side tracking
        // this.trackToApi("pageview", properties, accountType, eventId)
    }

    /**
     * Get the user ID for an event
     */
    public getEventUserId(eventId?: string): string | null {
        if (!eventId) return null

        return (
            this.eventUserIds[eventId] || this.getOrGenerateEventUserId(eventId)
        )
    }

    /**
     * Send event data to our API
     */
    private trackToApi(
        name: string,
        properties: Record<string, any>,
        accountType: AnalyticsAccountType,
        eventId?: string
    ): void {
        if (!this.apiEndpoint) return

        try {
            fetch(this.apiEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    type: "event",
                    name,
                    properties,
                    accountType,
                    eventId,
                    timestamp: new Date().toISOString(),
                }),
            }).catch((e) => console.error("Error sending event to API:", e))
        } catch (error) {
            console.error("Error sending event to API:", error)
        }
    }
}

// Create a singleton instance
export const analytics = new AnalyticsService()

export default analytics
